import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, KeyRound, Loader2, ChevronDown } from 'lucide-react';
import { api } from '../context/AuthContext';

const KEY = 'kp_mandant_ctx';
const waehlen = (nr) => { localStorage.setItem(KEY, nr); window.location.reload(); };

export const CodeEingeben = ({ onDone }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(() => localStorage.getItem('kp_zugangscode') || '');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const senden = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      const { data } = await api.post('/konto/verknuepfen', { code });
      setMsg({ ok: true, text: t('konto.code_ok', { nr: data.mandanten_nr }) }); setCode('');
      localStorage.setItem(KEY, data.mandanten_nr);
      localStorage.removeItem('kp_zugangscode');
      setTimeout(() => (onDone ? onDone() : window.location.reload()), 900);
    } catch (err) { setMsg({ ok: false, text: err?.response?.data?.detail || t('konto.code_fehler') }); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={senden} className="flex flex-wrap items-center gap-2" data-testid="code-eingeben-form">
      <KeyRound className="w-4 h-4 text-[#b8893d]" />
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX" maxLength={14} required
        className="font-mono tracking-widest text-sm text-[#0B1724] bg-white placeholder:text-gray-400 border border-gray-200 px-3 py-2 w-48 focus:border-[#b8893d] focus:outline-none" data-testid="code-eingeben-input" />
      <button type="submit" disabled={busy} className="px-4 py-2 text-xs tracking-wider bg-[#1B3A5C] text-white rounded-sm hover:bg-[#15304d] disabled:opacity-50" data-testid="code-eingeben-submit">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('konto.code_button')}
      </button>
      {msg && <span className={`text-xs w-full ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="code-eingeben-msg">{msg.text}</span>}
    </form>
  );
};

const Auswahl = ({ liste }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[1000] bg-[#0f2740]/75 flex items-center justify-center p-4" data-testid="kontext-auswahl">
      <div className="bg-white w-full max-w-md rounded-sm shadow-2xl border-t-4 border-[#b8893d] p-7">
        <h2 className="font-serif text-2xl text-[#1B3A5C]">{t('konto.auswahl_titel')}</h2>
        <div className="mt-5 space-y-2">
          {liste.map((m) => (
            <button key={m.mandanten_nr} type="button" onClick={() => waehlen(m.mandanten_nr)} data-testid={`kontext-option-${m.mandanten_nr}`}
              className="w-full flex items-center gap-3 p-4 border border-gray-200 hover:border-[#b8893d] hover:bg-[#fbf7f0] text-left transition-colors">
              <Building2 className="w-5 h-5 text-[#b8893d]" />
              <span className="flex-1"><span className="block text-[#1B3A5C] font-medium">{m.name}</span>
                <span className="text-xs text-[#7a8a99] font-mono">M {m.mandanten_nr} · {t(`konto.rolle_${m.role}`)}</span></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Mandanten-Kontext: 0 → Code-Eingabe, 1 → automatisch, mehrere → Pflicht-Auswahl + Wechsler. */
export const MandantKontext = () => {
  const { t } = useTranslation();
  const [liste, setListe] = useState(null);
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    api.get('/konto/mandanten').then(({ data }) => {
      const items = data.items || [];
      const akt = localStorage.getItem(KEY);
      if (items.length === 1 && akt !== items[0].mandanten_nr) waehlen(items[0].mandanten_nr);
      else if (akt && !items.some((m) => m.mandanten_nr === akt)) { localStorage.removeItem(KEY); if (items.length !== 1) window.location.reload(); }
      setListe(items);
    }).catch(() => setListe([]));
  }, []);
  if (!liste) return null;
  const akt = localStorage.getItem(KEY);
  const aktuell = liste.find((m) => m.mandanten_nr === akt);
  if (liste.length > 1 && !aktuell) return <Auswahl liste={liste} />;
  if (liste.length === 0) {
    return (
      <div className="bg-white border-l-4 border-[#b8893d] p-5 shadow-sm" data-testid="kontext-ohne">
        <p className="text-sm text-[#1B3A5C] font-medium">{t('konto.ohne_titel')}</p>
        <p className="text-xs text-[#34536b] mt-1 mb-3">{t('konto.ohne_text')}</p>
        <CodeEingeben />
      </div>
    );
  }
  return (
    <div className="relative inline-block" data-testid="kontext-wechsler">
      <button type="button" onClick={() => liste.length > 1 && setOffen(!offen)} data-testid="kontext-aktuell"
        className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/30 text-white text-xs rounded-sm hover:bg-white/20">
        <Building2 className="w-3.5 h-3.5" /> {aktuell?.name} · M {aktuell?.mandanten_nr} · {t(`konto.rolle_${aktuell?.role}`)}
        {liste.length > 1 && <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {offen && (
        <div className="absolute z-50 mt-1 w-72 bg-white shadow-xl border border-gray-100">
          {liste.map((m) => (
            <button key={m.mandanten_nr} type="button" onClick={() => waehlen(m.mandanten_nr)} data-testid={`kontext-switch-${m.mandanten_nr}`}
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#fbf7f0] ${m.mandanten_nr === akt ? 'text-[#b8893d] font-medium' : 'text-[#1B3A5C]'}`}>
              {m.name} <span className="font-mono text-xs text-[#7a8a99]">M {m.mandanten_nr}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
